import React, { useState, useEffect } from 'react';
import { updateProfile, updateEmail, updatePassword } from 'firebase/auth';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Container, Paper, Typography, TextField, Button, Grid, Box, Avatar, Alert, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, Stack, Switch, FormControlLabel, FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material';
import { Save, ManageAccounts, DownloadForOffline, PhoneIphone, LaptopWindows, WhatsApp } from '@mui/icons-material';

const normalizePhoneBR = (value = '') => {
  let digits = String(value).replace(/\D/g, '');
  if (digits.startsWith('55') && digits.length >= 12) {
    digits = digits.slice(2);
  }
  if (digits.length > 11) {
    digits = digits.slice(-11);
  }
  return digits;
};

const formatPhoneBR = (value = '') => {
  const digits = normalizePhoneBR(value).slice(0, 11);
  if (!digits) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

function Perfil({ user }) {
  const [nome, setNome] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState(''); // NOVO ESTADO
  const [novaSenha, setNovaSenha] = useState('');
  const [notificacaoAtiva, setNotificacaoAtiva] = useState(false);
  const [diasAntecedencia, setDiasAntecedencia] = useState('0');
  const [originalUsername, setOriginalUsername] = useState('');
  const [originalPhone, setOriginalPhone] = useState('');
  
  const [mensagem, setMensagem] = useState({ texto: '', tipo: '' });
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [openIosDialog, setOpenIosDialog] = useState(false);
  const [openDesktopDialog, setOpenDesktopDialog] = useState(false);
  const zapLink = 'https://wa.me/5562982373330';
  const zapNumeroExibicao = '(62) 98237-3330';

  useEffect(() => {
    if (user.displayName) setNome(user.displayName);
    if (user.email) setEmail(user.email);

    const fetchUserData = async () => {
      const docRef = doc(db, 'users', user.uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        if (docSnap.data().username) setUsername(docSnap.data().username);
        const telefoneAtual = docSnap.data().telefone_normalizado || docSnap.data().telefone || '';
        if (telefoneAtual) setWhatsapp(formatPhoneBR(telefoneAtual)); // PUXA O WHATSAPP
        setOriginalUsername(docSnap.data().username || '');
        setOriginalPhone(telefoneAtual || '');
        const alertas = docSnap.data().alertas || {};
        setNotificacaoAtiva(Boolean(alertas.notificacao_ativa));
        setDiasAntecedencia(
          alertas.dias_antecedencia !== undefined ? String(alertas.dias_antecedencia) : '0'
        );
      }
    };
    fetchUserData();
  }, [user]);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setMensagem({ texto: 'Aplicativo instalado com sucesso!', tipo: 'success' });
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleSalvar = async () => {
    setMensagem({ texto: '', tipo: '' });

    if (username.includes(' ')) {
      setMensagem({ texto: 'O Username não pode conter espaços.', tipo: 'error' });
      return;
    }

    const numeroLimpo = normalizePhoneBR(whatsapp);
    if (numeroLimpo && (numeroLimpo.length < 10 || numeroLimpo.length > 11)) {
      setMensagem({ texto: 'Digite um número de WhatsApp válido com DDD.', tipo: 'error' });
      return;
    }

    try {
      const usernameLimpo = username.toLowerCase().trim();

      if (usernameLimpo) {
        const usernameDoc = await getDoc(doc(db, 'usernames', usernameLimpo));
        if (usernameDoc.exists() && usernameDoc.data().uid !== user.uid) {
          throw new Error('Este username já está sendo usado por outra pessoa.');
        }
      }

      if (nome !== user.displayName) await updateProfile(user, { displayName: nome });
      if (email !== user.email) await updateEmail(user, email);
      if (novaSenha.length > 0) {
        if (novaSenha.length < 6) throw new Error('A nova senha deve ter pelo menos 6 caracteres.');
        await updatePassword(user, novaSenha);
      }

      await setDoc(doc(db, 'users', user.uid), {
        nome: nome,
        username: usernameLimpo,
        email: email,
        telefone: numeroLimpo, // ATUALIZA O WHATSAPP NO BANCO
        telefone_normalizado: numeroLimpo,
        telefone_formatado: formatPhoneBR(numeroLimpo),
        atualizadoEm: new Date().toISOString(),
        alertas: {
          notificacao_ativa: notificacaoAtiva,
          dias_antecedencia: Number(diasAntecedencia)
        }
      }, { merge: true });

      if (usernameLimpo) {
        await setDoc(doc(db, 'usernames', usernameLimpo), {
          uid: user.uid,
          email: email.toLowerCase(),
          atualizadoEm: new Date().toISOString()
        });
        if (originalUsername && originalUsername !== usernameLimpo) {
          await deleteDoc(doc(db, 'usernames', originalUsername));
        }
      }

      if (numeroLimpo) {
        await setDoc(doc(db, 'phones', numeroLimpo), {
          uid: user.uid,
          atualizadoEm: new Date().toISOString()
        });
        if (originalPhone && originalPhone !== numeroLimpo) {
          await deleteDoc(doc(db, 'phones', originalPhone));
        }
      }

      setOriginalUsername(usernameLimpo);
      setOriginalPhone(numeroLimpo);

      setMensagem({ texto: 'Perfil atualizado com sucesso!', tipo: 'success' });
      setNovaSenha('');

    } catch (error) {
      if (error.message.includes('username')) {
        setMensagem({ texto: error.message, tipo: 'error' });
      } else if (error.code === 'auth/requires-recent-login') {
        setMensagem({ texto: 'Por segurança, saia e faça login novamente para mudar senha ou e-mail.', tipo: 'error' });
      } else {
        setMensagem({ texto: 'Erro ao atualizar: ' + error.message, tipo: 'error' });
      }
    }
  };

  const handleInstalarApp = async () => {
    const isIos = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

    if (isStandalone) {
      setMensagem({ texto: 'O aplicativo já está instalado neste dispositivo.', tipo: 'info' });
      return;
    }

    if (deferredPrompt) {
      deferredPrompt.prompt();
      const escolha = await deferredPrompt.userChoice;
      if (escolha.outcome === 'accepted') {
        setMensagem({ texto: 'Instalação iniciada.', tipo: 'success' });
      } else {
        setMensagem({ texto: 'Instalação cancelada.', tipo: 'info' });
      }
      setDeferredPrompt(null);
      return;
    }

    if (isIos) {
      setOpenIosDialog(true);
    } else {
      setOpenDesktopDialog(true);
    }
  };

      return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <Snackbar open={!!mensagem.texto} autoHideDuration={5000} onClose={() => setMensagem({ texto: '', tipo: '' })} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={mensagem.tipo} sx={{ width: '100%' }}>{mensagem.texto}</Alert>
      </Snackbar>

      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: '#1a237e' }}>Meu Perfil</Typography>

      <Paper sx={{ p: 4, borderRadius: 4, bgcolor: '#fff', border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
          <Avatar sx={{ width: 80, height: 80, bgcolor: '#1a237e', fontSize: 30, mb: 2 }}>
            {nome ? nome[0].toUpperCase() : <ManageAccounts />}
          </Avatar>
          <Typography variant="h6" fontWeight="bold">{nome || 'Usuário'}</Typography>
          <Typography variant="body2" color="textSecondary">{email}</Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField fullWidth label="Nome Completo" value={nome} onChange={e => setNome(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Username (Login)" placeholder="ex: lucas.tech" value={username} onChange={e => setUsername(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="WhatsApp (com DDD)" placeholder="(62) 99999-9999" value={whatsapp} onChange={e => setWhatsapp(formatPhoneBR(e.target.value))} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth type="email" label="E-mail" value={email} onChange={e => setEmail(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth type="password" label="Nova Senha" placeholder="Deixe em branco para não alterar" value={novaSenha} onChange={e => setNovaSenha(e.target.value)} />
          </Grid>

          {/* Notificações de Contas a Pagar */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Notificações de Contas a Pagar
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificacaoAtiva}
                    onChange={(e) => setNotificacaoAtiva(e.target.checked)}
                  />
                }
                label="Receber alertas automáticos de vencimento (WhatsApp/Sistema)"
              />
              <FormControl
                fullWidth
                size="small"
                margin="normal"
                disabled={!notificacaoAtiva}
              >
                <InputLabel id="dias-antecedencia-label">Quando deseja ser avisado?</InputLabel>
                <Select
                  labelId="dias-antecedencia-label"
                  value={diasAntecedencia}
                  label="Quando deseja ser avisado?"
                  onChange={(e) => setDiasAntecedencia(e.target.value)}
                >
                  <MenuItem value="0">No dia do vencimento</MenuItem>
                  <MenuItem value="1">1 dia antes</MenuItem>
                  <MenuItem value="3">3 dias antes</MenuItem>
                  <MenuItem value="5">5 dias antes</MenuItem>
                </Select>
                <FormHelperText>Alertas são enviados apenas se você tiver WhatsApp cadastrado.</FormHelperText>
              </FormControl>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Button fullWidth variant="contained" size="large" startIcon={<Save />} onClick={handleSalvar} sx={{ bgcolor: '#1a237e', height: 50, fontWeight: 'bold' }}>
              Salvar Configurações
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: 2.5,
                bgcolor: '#f8fafc',
                borderColor: '#e2e8f0',
                boxShadow: '0 12px 28px rgba(15,23,42,0.05)',
                maxWidth: 620,
                mx: 'auto'
              }}
            >
              <Stack spacing={1.25} alignItems="flex-start">
                <Typography variant="subtitle1" fontWeight="bold" color="#0f172a">
                  Baixar aplicativo
                </Typography>
                <Typography variant="body2" color="#475569">
                  Instale no celular ou Windows para acesso mais rápido.
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<DownloadForOffline />}
                  onClick={handleInstalarApp}
                  sx={{
                    bgcolor: '#1d4ed8',
                    borderRadius: 2,
                    height: 46,
                    fontWeight: 700,
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#1a3fb3' }
                  }}
                >
                  Instalar app
                </Button>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: '100%' }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PhoneIphone />}
                    onClick={() => setOpenIosDialog(true)}
                    sx={{
                      borderRadius: 2,
                      height: 44,
                      borderColor: '#cbd5e1',
                      color: '#0f172a',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' }
                    }}
                  >
                    iPhone (iOS)
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<LaptopWindows />}
                    onClick={() => setOpenDesktopDialog(true)}
                    sx={{
                      borderRadius: 2,
                      height: 44,
                      borderColor: '#cbd5e1',
                      color: '#0f172a',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' }
                    }}
                  >
                    Windows
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: 2.5,
                bgcolor: '#f9fffb',
                borderColor: '#cdeee1',
                boxShadow: '0 12px 28px rgba(15,23,42,0.05)',
                maxWidth: 620,
                mx: 'auto'
              }}
            >
              <Stack spacing={1.2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 38, height: 38, borderRadius: '50%', bgcolor: '#22c55e', display: 'grid', placeItems: 'center', color: 'white', flexShrink: 0 }}>
                    <WhatsApp />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" color="#0f172a">Suporte via WhatsApp</Typography>
                    <Typography variant="body2" color="#475569">{zapNumeroExibicao}</Typography>
                  </Box>
                </Stack>
                <Typography variant="body2" color="#0f172a">
                  Fale com nosso bot ou equipe humana para dúvidas ou ajustes rápidos.
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<WhatsApp />}
                  onClick={() => window.open(zapLink, '_blank', 'noopener,noreferrer')}
                  sx={{
                    bgcolor: '#16a34a',
                    borderRadius: 2,
                    height: 46,
                    fontWeight: 700,
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#12803b' }
                  }}
                >
                  Abrir WhatsApp
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      <Dialog open={openIosDialog} onClose={() => setOpenIosDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>Instalar no iPhone (iOS)</DialogTitle>
        <DialogContent dividers>
          <Box>
            <Typography variant="body2">1. Abra o sistema no Safari.</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>2. Toque no botão de compartilhar.</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>3. Selecione "Adicionar à Tela de Início".</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenIosDialog(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDesktopDialog} onClose={() => setOpenDesktopDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>Instalar no Windows</DialogTitle>
        <DialogContent dividers>
          <Box>
            <Typography variant="body2">1. Abra o sistema no Google Chrome ou Microsoft Edge.</Typography>
            <Typography variant="body-2" sx={{ mt: 1 }}>2. Clique no ícone de instalação na barra de endereço.</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>3. Confirme em "Instalar".</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDesktopDialog(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Perfil;
